/**
 * Keyboard shortcut layer for the Studio DAW.
 *  - Space      play / pause
 *  - R          record / punch-in, or stop the take while recording
 *  - Ctrl/Cmd+Z undo
 *  - Ctrl/Cmd+Shift+Z or Ctrl/Cmd+Y  redo
 *  - S          stop
 *  - 0 / Home   rewind to start
 * Ignored while typing in inputs or when `disabled` (e.g. a dialog is open).
 */
import { useEffect, useRef } from 'react';

export function useStudioShortcuts(engine, disabled = false) {
  const engineRef = useRef(engine);
  engineRef.current = engine;
  const disabledRef = useRef(disabled);
  disabledRef.current = disabled;

  useEffect(() => {
    const onKeyDown = (e) => {
      if (disabledRef.current) return;
      const t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;

      const eng = engineRef.current;
      const { isPlaying, isRecording } = eng;
      const checking = eng.noiseCheck.status === 'checking';

      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (!isRecording && !checking && eng.canUndo) eng.undo();
        return;
      }
      // Redo: Ctrl/Cmd+Shift+Z or Ctrl/Cmd+Y
      if ((e.ctrlKey || e.metaKey) && !e.altKey && (
        (e.shiftKey && e.key.toLowerCase() === 'z') ||
        (!e.shiftKey && e.key.toLowerCase() === 'y')
      )) {
        e.preventDefault();
        if (!isRecording && !checking && eng.canRedo) eng.redo();
        return;
      }
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      switch (e.key === ' ' ? 'space' : e.key.toLowerCase()) {
        case 'space':
          e.preventDefault(); // stop the page from scrolling
          if (isRecording || checking) return;
          if (isPlaying) eng.pause(); else eng.play();
          break;
        case 'r':
          if (e.repeat || checking) return;
          if (isRecording) eng.stopRecording(); else eng.startRecording();
          break;
        case 's':
          if (isRecording || checking) return;
          eng.stop();
          break;
        case 'home':
        case '0':
          if (isRecording || checking) return;
          eng.seek(0);
          break;
        default:
          break;
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);
}
